import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  FiMenu,
  FiUser,
  FiLogOut,
  FiHome,
  FiBarChart2,
  FiShoppingCart,
  FiGrid,
  FiMap,
  FiMapPin,
  FiRefreshCw,
  FiTrendingUp,
  FiUsers,
  FiSettings,
  FiSliders,
  FiPackage,
  FiClock,
  FiDatabase,
  FiTarget,
  FiAlertTriangle,
} from "react-icons/fi";

const DashboardLayout = ({ children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(
    () => window.innerWidth >= 1024
  );
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const isAdmin = user?.isPrimary === true;

  // Set sidebar open/close based on screen size
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsSidebarOpen(true);
      } else {
        setIsSidebarOpen(false);
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleLogout = () => {
    logout();
  };

  const menuItems = [
    { path: "/dashboard", label: "Dashboard", icon: <FiHome size={20} /> },
    {
      path: "/analytics/product",
      label: "Product Analytics",
      icon: <FiBarChart2 size={20} />,
    },
    {
      path: "/analytics/sales-comparison",
      label: "Sales Comparison",
      icon: <FiTrendingUp size={20} />,
    },
    {
      path: "/analytics/hourly-sales",
      label: "Hourly Sales",
      icon: <FiClock size={20} />,
    },
    {
      path: "/analytics/shopify-campaign",
      label: "Shopify Campaign",
      icon: <FiTarget size={20} />,
    },
    {
      path: "/analytics/portal-wise-sales-trend",
      label: "Portal Wise Sales Trend",
      icon: <FiGrid size={20} />,
    },
    {
      path: "/analytics/zone-wise-sales-trend",
      label: "Zone Wise Sales Trend",
      icon: <FiMap size={20} />,
    },
    {
      path: "/analytics/state-wise-sales-trend",
      label: "State Wise Sales Trend",
      icon: <FiMapPin size={20} />,
    },
    {
      path: "/analytics/polling-status",
      label: "Polling Status",
      icon: <FiRefreshCw size={20} />,
    },
    { path: "/orders", label: "Orders", icon: <FiShoppingCart size={20} /> },
    { path: "/products", label: "Product Management", icon: <FiPackage size={20} /> },
    { path: "/platform", label: "Platform", icon: <FiSettings size={20} /> },
    { path: "/admin", label: "Admin Management", icon: <FiUsers size={20} /> },
    ...(isAdmin
      ? [
          {
            path: "/analytics/cron-logs",
            label: "Cron Logs",
            icon: <FiAlertTriangle size={20} />,
          },
          { path: "/backup", label: "Backup", icon: <FiDatabase size={20} /> },
          { path: "/settings", label: "Settings", icon: <FiSliders size={20} /> },
        ]
      : []),
  ];

  return (
    <div className="bg-gray-50 min-h-screen font-sans">
      {/* Mobile Backdrop Overlay */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-30 w-64 bg-gray-900 text-white transform transition-transform duration-300 ease-in-out flex flex-col ${
          isSidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {/* Fixed Header */}
        <div className="flex items-center justify-between h-16 px-6 border-b border-gray-800 flex-shrink-0">
          <Link
            to="/dashboard"
            onClick={() => {
              // Close sidebar on mobile after navigating
              if (window.innerWidth < 1024) setIsSidebarOpen(false);
            }}
            className="inline-flex items-center"
          >
            <img
              src="/logo-palsons-derma.png"
              alt="Palsons Derma Logo"
              className="h-8 w-auto cursor-pointer"
            />
          </Link>
          {/* Close button for mobile */}
          <button
            onClick={() => setIsSidebarOpen(false)}
            className="lg:hidden p-2 rounded-full hover:bg-gray-800 transition-colors duration-200"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        
        {/* Scrollable Navigation */}
        <nav className="flex-1 overflow-y-auto mt-6">
          {menuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => {
                // Close sidebar on mobile when clicking a menu item
                if (window.innerWidth < 1024) {
                  setIsSidebarOpen(false);
                }
              }}
              className={`flex items-center px-6 py-3 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors duration-200 ${
                location.pathname === item.path
                  ? "bg-gray-800 text-white border-l-4 border-blue-500"
                  : ""
              }`}
            >
              <span className="mr-3">{item.icon}</span>
              {item.label}
            </Link>
          ))}
        </nav>
      </div>

      {/* Main Content */}
      <div
        className={`flex-1 transition-all duration-300 ${
          isSidebarOpen ? "lg:ml-64" : "ml-0"
        }`}
      >
        {/* Top Navigation Bar */}
        <header className="bg-gradient-to-r from-[#049ED5] to-[#4c52a6] text-white shadow-lg sticky top-0 z-[10000]">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center">
              <button
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                className="p-2 rounded-full hover:bg-blue-700 transition-colors duration-200 mr-4"
              >
                <FiMenu size={20} />
              </button>
              <h2 className="text-xl font-semibold">
                {menuItems.find((item) => item.path === location.pathname)
                  ?.label || "Dashboard"}
              </h2>
            </div>

            <div className="flex items-center space-x-4">
              <div className="relative">
                <button
                  onClick={() => setIsProfileOpen(!isProfileOpen)}
                  className="flex items-center space-x-2 focus:outline-none"
                >
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                    <FiUser className="text-gray-600" />
                  </div>
                </button>

                {/* Dropdown Menu */}
                {isProfileOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl py-1 z-50 text-gray-800">
                    <button
                      onClick={() => {
                        navigate("/profile");
                        setIsProfileOpen(false);
                      }}
                      className="flex items-center px-4 py-2 text-sm hover:bg-gray-100 w-full text-left transition-colors duration-200"
                    >
                      <FiUser className="mr-2" />
                      Profile
                    </button>
                    <button
                      onClick={() => {
                        handleLogout();
                        setIsProfileOpen(false);
                      }}
                      className="flex items-center px-4 py-2 text-sm text-red-600 hover:bg-gray-100 w-full text-left transition-colors duration-200"
                    >
                      <FiLogOut className="mr-2" />
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Main Content Area */}
        <main className="p-6">{children}</main>
      </div>
    </div>
  );
};

export default DashboardLayout;
